import React, { useState, useEffect, useRef } from "react";
import ProductCard from "./ProductCard";
import Icon from "../../../components/AppIcon";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton"; // استيراد Skeleton

const ProductGrid = ({
  products,
  loading,
  hasMore,
  onLoadMore,
  onAddToCart,
  onToggleWishlist,
  viewMode = "grid",
  total,
}) => {
  const [loadingMore, setLoadingMore] = useState(false);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const { t } = useTranslation();

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadingMore]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      await onLoadMore();
    } catch (error) {
      console.error("Error loading more products:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const renderSkeletonCard = () => (
    <div className="bg-white border  border-gray-300 rounded-lg overflow-hidden shadow-lg">
      <Skeleton height={200} />
      <div className="p-4">
        <Skeleton height={20} count={2} />
      </div>
    </div>
  );

  const renderSkeletonGrid = () => (
    <div className={`grid gap-4 pt-24 ${
      viewMode === "grid"
        ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
        : "grid-cols-1 md:grid-cols-2"
    }`}>
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index}>{renderSkeletonCard()}</div>
      ))}
    </div>
  );

  if (loading && products.length === 0) {
    return <div className="space-y-6">{renderSkeletonGrid()}</div>;
  }

  if (!loading && products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Icon
          name="Search"
          size={64}
          className="text-text-secondary opacity-50 mb-4"
        />
        <h3 className="text-xl font-medium text-text-primary mb-2">
          {t("noProductsFound")}
        </h3>
        <p className="text-text-secondary mb-6 max-w-md">
          {t("noProductsMessage")}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => window.location.reload()}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-smooth"
          >
            <Icon name="RotateCcw" size={16} />
            <span>{t("resetFilters")}</span>
          </button>
          <button
            onClick={() => window.history.back()}
            className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="ArrowLeft" size={16} />
            <span>{t("goBack")}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Products Grid */}
      <div
        className={`grid gap-4 pt-20 ${
          viewMode === "grid"
            ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
            : "grid-cols-1 md:grid-cols-2"
        }`}
      >
        {products?.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onAddToCart={onAddToCart}
            onToggleWishlist={onToggleWishlist}
          />
        ))}
      </div>

      {hasMore && (
        <div ref={loadMoreRef} className="flex justify-center py-8">
          {loadingMore ? (
            <div className="flex items-center space-x-3 text-text-secondary">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent"></div>
              <span>{t("loadingMoreProducts")}</span>
            </div>
          ) : (
            <button
              onClick={handleLoadMore}
              className="flex items-center space-x-2 px-6 py-3 bg-surface border border-border rounded-lg hover:bg-muted transition-smooth focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <Icon name="Plus" size={16} />
              <span>{t("loadMoreProducts")}</span>
            </button>
          )}
        </div>
      )}

      {/* End of Results */}
      {!hasMore && products.length > 0 && (
        <div className="text-center py-8 border-t border-border">
          <Icon
            name="CheckCircle"
            size={24}
            className="text-success mx-auto mb-2"
          />
          <p className="text-text-secondary">
            {t("seenAllProducts", { count: total })}
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
